import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';

const PAGE_SIZE = 10;

export default function ManageOperators({ onEdit, onManageMembers, user }) {
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [toast, setToast] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [profileOp, setProfileOp] = useState(null);
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);

    const stats = useMemo(() => {
        const total = operators.length;
        const approved = operators.filter(op => (op.status || 'Approved') === 'Approved').length;
        const inactive = operators.filter(op => op.status === 'Inactive').length;
        return { total, approved, inactive };
    }, [operators]);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };


    const fetchOperators = () => {
        setLoading(true);
        fetch('/api/operators')
            .then(r => r.json())
            .then(data => { setOperators(data); setLoading(false); })
            .catch(() => setLoading(false));
    };


    const fetchSubscriptions = () => {
        fetch('/api/subscriptions')
            .then(r => r.json())
            .then(data => setSubscriptions(data))
            .catch(err => console.error('Error fetching subscriptions:', err));
    };

    useEffect(() => {
        fetchOperators();
        fetchSubscriptions();
    }, []);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return operators.filter(op =>
            (op.companyName || '').toLowerCase().includes(q) ||
            (op.country || '').toLowerCase().includes(q) ||
            (op.industry || '').toLowerCase().includes(q) ||
            (op.primaryPhone || '').includes(q)
        );
    }, [operators, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageData = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

    const handleDelete = async (op) => {
        if (!window.confirm(`Delete "${op.companyName}"? This cannot be undone.`)) return;
        setDeletingId(op._id);
        try {
            const res = await fetch(`/api/operators/${op._id}`, {
                method: 'DELETE',
                headers: {
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                }
            });
            if (res.ok) {
                setOperators(prev => prev.filter(o => o._id !== op._id));
                if (selected && selected._id === op._id) setSelected(null);
                showToast(`"${op.companyName}" deleted successfully.`, 'success');
            } else {
                const data = await res.json();
                showToast(data.message || 'Failed to delete.', 'error');
            }
        } catch {
            showToast('Server error. Is the backend running?', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleEdit = (op) => {
        if (onEdit) onEdit(op);
    };

    const toggleStatus = async (op) => {
        const currentStatus = op.status || 'Approved';
        const newStatus = currentStatus === 'Approved' ? 'Inactive' : 'Approved';
        try {
            const res = await fetch(`/api/operators/${op._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'adminName': `${user?.firstName} ${user?.lastName}`,
                    'adminEmail': user?.email
                },
                body: JSON.stringify({ status: newStatus }),
            });
            if (res.ok) {
                setOperators(prev => prev.map(o => o._id === op._id ? { ...o, status: newStatus } : o));
                showToast(`Status updated to ${newStatus}`);
            }
        } catch (err) {
            console.error('Failed to update status:', err);
            showToast('Failed to update status', 'error');
        }
    };

    const handleViewProfile = (op) => {
        setProfileOp(op);
    };

    const handleManageMembers = (op) => {
        if (onManageMembers) onManageMembers(op);
    };

    const getOpSubscription = (opId) => {
        return subscriptions.find(s => (s.operatorId?._id || s.operatorId) === opId);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Company Name': 'ViaRidez Transport By Bus Rental L.L.C',
                'Category': 'Transportation',
                'Industry': 'Transport',
                'Registration Number': 'CN-123456',
                'Website': 'www.example.ae',
                'Street Address': 'Street 1, Dubai',
                'City': 'Dubai',
                'State Province': 'Dubai',
                'Postal Code': '12345',
                'Country': 'United Arab Emirates',
                'Primary Phone': '+971501234567',
                'Primary Email': 'info@example.ae',
                'Password': 'password123',
                'Contact Person Name': 'John Doe',
                'Contact Person Phone': '+971509876543',
                'Tax ID': 'TRN-100123456789003',
                'Default Currency': 'UAE Dirham (AED)',
                'VAT Percentage': '5',
                'Fiscal Year Start': '2024-01-01',
                'Bank Name': 'Emirates NBD',
                'Account Holder Name': 'ViaRidez Transport L.L.C',
                'Account Name': 'Corporate Account',
                'Account Number': '1234567890',
                'IBAN': 'AE070331234567890123456',
                'SWIFT Code': 'EBILAEAD',
                'Default Shift Start': '08:00',
                'Default Shift End': '17:00',
                'Fleet Size': '25',
                'Number of Drivers': '30',
                'Primary Service Type': 'Employee Transportation, School Transportation',
                'Timezone': 'Asia/Dubai',
                'Emergency Contact Name': 'Jane Smith',
                'Emergency Phone': '+971501122334'
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Operators Template');
        XLSX.writeFile(workbook, 'Operators_Import_Template.xlsx');
    };

    const handleImportExcel = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    showToast('The Excel file is empty.', 'error');
                    setImporting(false);
                    return;
                }

                // Map Excel fields to DB fields
                const mappedData = data.map(row => ({
                    companyName: row['Company Name'],
                    category: row['Category'],
                    industry: row['Industry'],
                    registrationNumber: row['Registration Number'],
                    website: row['Website'],
                    streetAddress: row['Street Address'],
                    city: row['City'],
                    stateProvince: row['State Province'],
                    postalCode: row['Postal Code'],
                    country: row['Country'],
                    primaryPhone: row['Primary Phone'],
                    primaryEmail: row['Primary Email'],
                    password: row['Password'],
                    contactPersonName: row['Contact Person Name'],
                    contactPersonPhone: row['Contact Person Phone'],
                    taxId: row['Tax ID'],
                    defaultCurrency: row['Default Currency'],
                    vatPercentage: row['VAT Percentage'],
                    fiscalYearStart: row['Fiscal Year Start'],
                    bankName: row['Bank Name'],
                    accountHolderName: row['Account Holder Name'],
                    accountName: row['Account Name'],
                    accountNumber: row['Account Number'],
                    iban: row['IBAN'],
                    swiftCode: row['SWIFT Code'],
                    defaultShiftStart: row['Default Shift Start'],
                    defaultShiftEnd: row['Default Shift End'],
                    fleetSize: row['Fleet Size'],
                    numberOfDrivers: row['Number of Drivers'],
                    primaryServiceType: row['Primary Service Type'],
                    timezone: row['Timezone'],
                    emergencyContactName: row['Emergency Contact Name'],
                    emergencyPhone: row['Emergency Phone']
                }));

                const res = await fetch('/api/operators/import', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'adminName': `${user?.firstName} ${user?.lastName}`,
                        'adminEmail': user?.email
                    },
                    body: JSON.stringify(mappedData),
                });

                const result = await res.json();
                if (res.ok) {
                    showToast(`${result.successCount} operators imported successfully!`, 'success');
                    fetchOperators();
                } else {
                    showToast(result.message || 'Import failed.', 'error');
                }
            } catch (error) {
                console.error('Import error:', error);
                showToast('Failed to parse Excel file.', 'error');
            } finally {
                setImporting(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100-8 4 4 0 000 8z',
        check: 'M22 11.08V12a10 10 0 11-5.93-9.14 M22 4L12 14.01l-3-3',
        team: 'M17 21v-2a4 4 0 00-3-3.87 M9 7a4 4 0 100-8 4 4 0 000 8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
        slash: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z M12 9v4 M12 17h.01',
        edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
        trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
        external: 'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6 M15 3h6v6 M10 14L21 3',
        plus: 'M12 5v14 M5 12h14',
        search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
        building: 'M3 21h18M3 7v14m18-14v14M3 7l9-4 9 4M5 21V11m4 10V11m6 10V11m4 10V11',
        mapPin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1118 0z M12 10a3 3 0 10-6 0 3 3 0 006 0z',
        dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
        settings: 'M12 15a3 3 0 100-6 3 3 0 000 6z M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z',
        image: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2z M8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z M21 15l-5-5L5 21',
        userPlus: 'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100-8 4 4 0 000 8z M19 8v6 M16 11h6',
        lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 00 2 2h14a2 2 0 00 2-2v-7a2 2 0 00-2-2z M7 11V7a5 5 0 0110 0v4',
        mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6',
        driver: ['M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M17 11l2 2 4-4'],
        upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
        download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    };


    return (
        <div className="mo-page">
            <style>{`
                .mo-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .mo-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .mo-title { font-size: 24px; font-weight: 700; color: #1e293b; margin: 0; }
                .add-client-btn { 
                    background: #3f51b5; color: white; border: none; padding: 10px 20px; 
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s;
                }
                .add-client-btn:hover { background: #303f9f; }

                /* Premium Buttons */
                .premium-btn {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    border: none;
                    color: white;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    white-space: nowrap;
                }
                .premium-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
                }
                .premium-btn:active {
                    transform: translateY(0);
                }
                .btn-import {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }
                .btn-template {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                }
                .btn-primary {
                    background: #1e1b4b;
                    box-shadow: 0 4px 12px rgba(30, 27, 75, 0.25);
                }
                .premium-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 30px; }
                .stat-card { 
                    background: white; padding: 20px; border-radius: 12px; display: flex; align-items: center; gap: 16px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05); border: 1px solid #f1f5f9;
                }
                .stat-icon { 
                    width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                    font-size: 20px;
                }
                .stat-info .stat-val { font-size: 22px; font-weight: 700; color: #1e293b; display: block; }
                .stat-info .stat-lbl { font-size: 13px; color: #64748b; font-weight: 500; }

                .mo-toolbar { 
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; 
                    background: white; padding: 16px 20px; border-radius: 12px; border: 1px solid #f1f5f9;
                }
                .per-page { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #64748b; }
                .per-page select { padding: 6px 12px; border: 1px solid #e2e8f0; border-radius: 6px; outline: none; }
                .search-box { position: relative; width: 300px; }
                .search-box input { 
                    width: 100%; padding: 8px 12px 8px 36px; border: 1px solid #e2e8f0; border-radius: 8px; 
                    font-size: 13px; outline: none; transition: border-color 0.2s;
                }
                .search-box input:focus { border-color: #3f51b5; }
                .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }

                .table-container { 
                    background: white; border-radius: 12px; overflow: hidden; border: 1px solid #f1f5f9;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                }
                .mo-table { width: 100%; border-collapse: collapse; }
                .mo-table th { 
                    background: #f0f4ff; padding: 14px 20px; text-align: left; font-size: 13px; 
                    font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0;
                }
                .mo-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .mo-table tr:hover { background: #f8fafc; }

                .name-cell { display: flex; align-items: center; gap: 12px; }
                .logo-box { 
                    width: 40px; height: 40px; background: #f1f5f9; border-radius: 8px;
                    display: flex; align-items: center; justify-content: center; overflow: hidden;
                    border: 1px solid #e2e8f0;
                }
                .logo-box img { width: 100%; height: 100%; object-fit: cover; }
                .company-name { 
                    font-weight: 600; color: #1e293b; font-size: 14px; 
                    text-decoration: none; transition: all 0.2s;
                }
                a.company-name:hover { color: #3f51b5; text-decoration: underline; cursor: pointer; }

                .plan-col { font-size: 12px; line-height: 1.5; }
                .plan-title { font-weight: 600; color: #475569; margin-bottom: 4px; }
                .plan-date { color: #64748b; }

                /* Toggle Switch */
                .switch { position: relative; display: inline-block; width: 44px; height: 22px; }
                .switch input { opacity: 0; width: 0; height: 0; }
                .slider { 
                    position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; 
                    background-color: #cbd5e1; transition: .4s; border-radius: 34px; 
                }
                .slider:before { 
                    position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; 
                    background-color: white; transition: .4s; border-radius: 50%; 
                }
                input:checked + .slider { background-color: #3f51b5; }
                input:checked + .slider:before { transform: translateX(22px); }

                .action-btns { display: flex; align-items: center; gap: 12px; }
                .action-icon-btn { 
                    background: none; border: none; cursor: pointer; color: #94a3b8; 
                    transition: color 0.2s; padding: 4px;
                }
                .action-icon-btn:hover { color: #3f51b5; }
                .action-icon-btn.delete:hover { color: #ef4444; }

                .pagination-footer { 
                    margin-top: 20px; display: flex; justify-content: space-between; align-items: center;
                    padding: 0 10px;
                }
                .page-info { font-size: 13px; color: #64748b; }
                .page-ctrls { display: flex; gap: 8px; }
                .page-btn { 
                    padding: 6px 12px; border: 1px solid #e2e8f0; background: white; border-radius: 6px;
                    font-size: 13px; color: #475569; cursor: pointer; transition: all 0.2s;
                }
                .page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #cbd5e1; }
                .page-btn.active { background: #3f51b5; color: white; border-color: #3f51b5; }
                .page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

                .op-toast {
                    position: fixed; top: 20px; right: 20px; padding: 12px 24px; border-radius: 8px;
                    background: #333; color: white; z-index: 1000; animation: slideIn 0.3s ease-out;
                }
                .op-toast.success { background: #10b981; }
                .op-toast.error { background: #ef4444; }
                @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.75); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 2000;
                    padding: 20px; animation: fadeIn 0.3s ease-out;
                }
                .profile-modal {
                    background: white; width: 100%; max-width: 900px; max-height: 90vh;
                    border-radius: 20px; overflow-hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                    display: flex; flex-direction: column; animation: modalUp 0.3s ease-out;
                }
                .modal-header {
                    padding: 24px 32px; border-bottom: 1px solid #f1f5f9;
                    display: flex; justify-content: space-between; align-items: center;
                    background: #f8fafc; border-radius: 20px 20px 0 0;
                }
                .modal-body { padding: 32px; overflow-y: auto; flex: 1; }
                .modal-close { 
                    background: #f1f5f9; border: none; width: 32px; height: 32px; 
                    border-radius: 50%; cursor: pointer; color: #64748b;
                    display: flex; align-items: center; justify-content: center; transition: all 0.2s;
                }
                .modal-close:hover { background: #e2e8f0; color: #1e293b; }

                .profile-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 40px; }
                .profile-sidebar { display: flex; flex-direction: column; align-items: center; text-align: center; }
                .profile-logo-large {
                    width: 120px; height: 120px; border-radius: 24px; background: #f1f5f9;
                    display: flex; align-items: center; justify-content: center; margin-bottom: 20px;
                    border: 1px solid #e2e8f0; overflow: hidden;
                }
                .profile-logo-large img { width: 100%; height: 100%; object-fit: cover; }
                .profile-main-info h2 { font-size: 20px; font-weight: 700; color: #1e293b; margin: 0 0 8px 0; }
                .profile-badge {
                    display: inline-block; padding: 4px 12px; border-radius: 99px; font-size: 12px; font-weight: 600;
                    margin-bottom: 20px;
                }
                .badge-approved { background: #ecfdf5; color: #10b981; }
                .badge-inactive { background: #fef2f2; color: #ef4444; }

                .info-section { margin-bottom: 32px; }
                .info-section-title { 
                    font-size: 14px; font-weight: 700; color: #3f51b5; text-transform: uppercase; 
                    letter-spacing: 0.05em; margin-bottom: 16px; display: flex; align-items: center; gap: 8px;
                }
                .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .info-item { background: #f8fafc; padding: 12px 16px; border-radius: 10px; border: 1px solid #f1f5f9; }
                .info-label { font-size: 11px; color: #64748b; font-weight: 500; margin-bottom: 4px; display: block; }
                .info-val { font-size: 14px; color: #1e293b; font-weight: 600; word-break: break-all; }

                /* --- Dark Mode Overrides --- */
                .theme-dark .mo-page {
                    background: #0f0f0f;
                }
                .theme-dark .mo-title {
                    color: #fff;
                }
                .theme-dark .stat-card {
                    background: #1a1a1a;
                    border-color: #242424;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3);
                }
                .theme-dark .stat-info .stat-val {
                    color: #fff;
                }
                .theme-dark .stat-info .stat-lbl {
                    color: #888;
                }
                .theme-dark .mo-toolbar {
                    background: #161616;
                    border-color: #222;
                }
                .theme-dark .search-box input {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .per-page select {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .table-container {
                    background: #161616;
                    border-color: #222;
                }
                .theme-dark .mo-table th {
                    background: #1e1e1e;
                    color: #888;
                    border-bottom-color: #2a2a2a;
                }
                .theme-dark .mo-table td {
                    border-bottom-color: #222;
                    color: #ccc;
                }
                .theme-dark .mo-table tr:hover {
                    background: #1a1a1a;
                }
                .theme-dark .company-name {
                    color: #fff;
                }
                .theme-dark .logo-box {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                }
                .theme-dark .plan-title {
                    color: #ccc;
                }
                .theme-dark .slider {
                    background-color: #2a2a2a;
                }
                .theme-dark .page-btn {
                    background: #1e1e1e;
                    border-color: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .page-btn:hover:not(:disabled) {
                    background: #2a2a2a;
                }
                .theme-dark .profile-modal {
                    background: #1a1a1a;
                    border: 1px solid #242424;
                }
                .theme-dark .modal-header {
                    background: #1e1e1e;
                    border-bottom-color: #242424;
                }
                .theme-dark .modal-header h3 {
                    color: #fff !important;
                }
                .theme-dark .modal-close {
                    background: #2a2a2a;
                    color: #aaa;
                }
                .theme-dark .info-item {
                    background: #1e1e1e;
                    border-color: #242424;
                }
                .theme-dark .info-val {
                    color: #eee;
                }
                .theme-dark .info-label {
                    color: #888;
                }
                .theme-dark .profile-logo-large {
                    background: #1e1e1e;
                    border-color: #242424;
                }
                .theme-dark .profile-main-info h2 {
                    color: #fff;
                }
                .theme-dark .badge-approved {
                    background: rgba(16, 185, 129, 0.1);
                    color: #10b981;
                }
                .theme-dark .badge-inactive {
                    background: rgba(239, 68, 68, 0.1);
                    color: #ef4444;
                }
                .profile-service-badge {
                    display: flex; align-items: center; gap: 6px; padding: 4px 10px; background: white;
                    border: 1px solid #e2e8f0; border-radius: 6px; font-size: 12px; font-weight: 500; color: #475569;
                }
            `}</style>

            {toast && <div className={`op-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="mo-header">
                <h1 className="mo-title">Operators List</h1>
                <div style={{ display: 'flex', gap: 12 }}>
                    {(user?.userType === 'admin' || user?.permissions?.includes('Create Operator')) && (
                        <>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImportExcel}
                                accept=".xlsx, .xls, .csv"
                                style={{ display: 'none' }}
                            />
                            <button className="premium-btn btn-import" onClick={() => fileInputRef.current.click()} disabled={importing}>
                                <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                                {importing ? 'Importing...' : 'Import Operators'}
                            </button>
                            <button className="premium-btn btn-template" onClick={downloadTemplate}>
                                <Icon d={icons.download} size={18} strokeWidth={2.5} />
                                Template
                            </button>
                            <button className="premium-btn btn-primary" onClick={() => (onEdit && onEdit(null))}>
                                <Icon d={icons.plus} size={18} strokeWidth={3} />
                                Add Operators
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#f0f4ff', color: '#3f51b5' }}>
                        <Icon d={icons.users} size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{stats.total}</span>
                        <span className="stat-lbl">Total Operators</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                        <Icon d={icons.check} size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{stats.approved}</span>
                        <span className="stat-lbl">Approved Operators</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#fff7ed', color: '#f97316' }}>
                        <Icon d={icons.slash} size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-val">{stats.inactive}</span>
                        <span className="stat-lbl">Inactive Operators</span>
                    </div>
                </div>
            </div>

            <div className="mo-toolbar">
                <div className="per-page">
                    <select>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </select>
                    <span>Operators Per Page</span>
                </div>
                <div className="search-box">
                    <span className="search-icon"><Icon d={icons.search} size={14} color="#94a3b8" /></span>
                    <input
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={handleSearch}
                    />
                </div>
            </div>

            <div className="table-container">
                <table className="mo-table">
                    <thead>
                        <tr>
                            <th style={{ width: 60 }}>S.No</th>
                            <th style={{ width: 80 }}>Logo</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Plan</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.length === 0 ? (
                            <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>No Operators found.</td></tr>
                        ) : (
                            pageData.map((op, idx) => {
                                const sub = getOpSubscription(op._id);
                                return (
                                    <tr key={op._id}>
                                        <td>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                                        <td>
                                            <div className="logo-box">
                                                {op.companyLogo ? (
                                                    <img src={`/api/uploads/${op.companyLogo}`} alt="logo" />
                                                ) : (
                                                    <Icon d={icons.image} color="#94a3b8" />
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <a href="#" className="company-name" onClick={(e) => { e.preventDefault(); handleViewProfile(op); }}>
                                                {op.companyName || '—'}
                                            </a>
                                        </td>
                                        <td>{op.primaryEmail || '—'}</td>
                                        <td>
                                            <div style={{ color: '#1e293b', fontWeight: 500 }}>{op.primaryPhone || '—'}</div>
                                            <div style={{ fontSize: 12.25, color: '#64748b' }}>{op.country || 'India'}</div>
                                        </td>
                                        <td>
                                            {sub ? (
                                                <div className="plan-col">
                                                    <div className="plan-title">{sub.planId?.name}</div>
                                                    <div className="plan-date">Purchase Date: {new Date(sub.startDate).toISOString().split('T')[0]}</div>
                                                    <div className="plan-date">Expiration Date: {new Date(sub.nextBillingDate).toISOString().split('T')[0]}</div>
                                                </div>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: 13.36 }}>No Active Plan</span>
                                            )}
                                        </td>
                                        <td>
                                            <label className="switch">
                                                <input
                                                    type="checkbox"
                                                    checked={(op.status || 'Approved') === 'Approved'}
                                                    onChange={() => toggleStatus(op)}
                                                />
                                                <span className="slider"></span>
                                            </label>
                                        </td>
                                        <td>
                                            <div className="action-btns" style={{ justifyContent: 'center' }}>
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Edit Operator')) && (
                                                    <button className="action-icon-btn" title="Edit" onClick={() => handleEdit(op)}>
                                                        <Icon d={icons.edit} size={14} />
                                                    </button>
                                                )}
                                                {(user?.userType === 'admin' || user?.permissions?.includes('Delete Operator')) && (
                                                    <button className="action-icon-btn delete" title="Delete" onClick={() => handleDelete(op)}>
                                                        <Icon d={icons.trash} size={14} />
                                                    </button>
                                                )}
                                                <button className="action-icon-btn" title="Add Members" onClick={() => handleManageMembers(op)}>
                                                    <Icon d={icons.team} size={14} />
                                                </button>

                                                <button className="action-icon-btn" title="View Profile" onClick={() => handleViewProfile(op)}>
                                                    <Icon d={icons.external} size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {!loading && filtered.length > PAGE_SIZE && (
                <div className="pagination-footer">
                    <div className="page-info">
                        Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(filtered.length, page * PAGE_SIZE)} of {filtered.length} entries
                    </div>
                    <div className="page-ctrls">
                        <button className="page-btn" disabled={page === 1} onClick={() => setPage(1)}>Previous</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                className={`page-btn ${page === p ? 'active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                        <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                    </div>
                </div>
            )}

            {/* Profile Modal */}
            {profileOp && (
                <div className="modal-overlay" onClick={() => setProfileOp(null)}>
                    <div className="profile-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ width: 8, height: 24, background: '#3f51b5', borderRadius: 4 }}></div>
                                <h3 style={{ margin: 0, fontSize: 20.05, fontWeight: 700, color: '#1e293b' }}>Operator Profile</h3>
                            </div>
                            <button className="modal-close" onClick={() => setProfileOp(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div className="profile-grid">
                                <div className="profile-sidebar">
                                    <div className="profile-logo-large">
                                        {profileOp.companyLogo ? (
                                            <img src={`/api/uploads/${profileOp.companyLogo}`} alt="logo" />
                                        ) : (
                                            <Icon d={icons.image} size={48} color="#94a3b8" />
                                        )}
                                    </div>
                                    <div className="profile-main-info">
                                        <h2>{profileOp.companyName}</h2>
                                        <div className={`profile-badge ${(profileOp.status || 'Approved') === 'Approved' ? 'badge-approved' : 'badge-inactive'}`}>
                                            {(profileOp.status || 'Approved')}
                                        </div>
                                    </div>
                                    <div style={{ padding: '0 20px', textAlign: 'left', width: '100%' }}>
                                        <div className="info-item" style={{ marginBottom: 12 }}>
                                            <span className="info-label">Industry</span>
                                            <span className="info-val">{profileOp.industry || '—'}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Registration No.</span>
                                            <span className="info-val">{profileOp.registrationNumber || '—'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="profile-content">
                                    <div className="info-section">
                                        <div className="info-section-title">
                                            <Icon d={icons.mapPin} size={14} color="#3f51b5" />
                                            Address & Contact
                                        </div>
                                        <div className="info-grid">
                                            <div className="info-item" style={{ gridColumn: 'span 2' }}>
                                                <span className="info-label">Street Address</span>
                                                <span className="info-val">{profileOp.streetAddress || '—'}</span>
                                            </div>
                                            <div className="info-item"><span className="info-label">City</span><span className="info-val">{profileOp.city || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">Country</span><span className="info-val">{profileOp.country || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">Email</span><span className="info-val">{profileOp.primaryEmail || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">Phone</span><span className="info-val">{profileOp.primaryPhone || '—'}</span></div>
                                        </div>
                                    </div>

                                    <div className="info-section">
                                        <div className="info-section-title">
                                            <Icon d={icons.dollar} size={14} color="#3f51b5" />
                                            Financial Details
                                        </div>
                                        <div className="info-grid">
                                            <div className="info-item"><span className="info-label">Tax ID</span><span className="info-val">{profileOp.taxId || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">Currency</span><span className="info-val">{profileOp.defaultCurrency || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">Bank Name</span><span className="info-val">{profileOp.bankName || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">Account Holder</span><span className="info-val">{profileOp.accountHolderName || '—'}</span></div>
                                            <div className="info-item"><span className="info-label">IBAN</span><span className="info-val">{profileOp.iban || '—'}</span></div>
                                        </div>
                                    </div>

                                    <div className="info-section" style={{ marginBottom: 0 }}>
                                        <div className="info-section-title">
                                            <Icon d={icons.settings} size={14} color="#3f51b5" />
                                            Operations
                                        </div>
                                        <div className="info-grid">
                                            <div className="info-item"><span className="info-label">Fleet Size</span><span className="info-val">{profileOp.fleetSize || '0'} Vehicles</span></div>
                                            <div className="info-item"><span className="info-label">Drivers</span><span className="info-val">{profileOp.numberOfDrivers || '0'} Personnel</span></div>
                                            <div className="info-item" style={{ gridColumn: 'span 2' }}>
                                                <span className="info-label">Service Types</span>
                                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                                    {Array.isArray(profileOp.primaryServiceType) && profileOp.primaryServiceType.length > 0 ? (
                                                        profileOp.primaryServiceType.map(s => (
                                                            <span key={s} className="profile-service-badge">
                                                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1' }}></div>
                                                                {s}
                                                            </span>
                                                        ))
                                                    ) : '—'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '20px 32px', borderTop: '1px solid #f1f5f9', textAlign: 'right', background: '#f8fafc', borderRadius: '0 0 20px 20px' }}>
                            <button
                                onClick={() => setProfileOp(null)}
                                style={{ padding: '10px 24px', background: '#3f51b5', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
                            >
                                Close Profile
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
